import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../logo.svg';
import { useAuth } from '../auth/AuthContext';
import { ErrorBanner } from '../components/ui';
import { IconArrowRight, IconLock, IconMail, IconShieldCheck, IconSliders, IconTrendUp, IconEye, IconEyeOff } from '../components/icons';

const FEATURES = [
  {
    icon: IconTrendUp,
    title: 'Meta Lead Ads, in real time',
    body: 'Every form submission lands as a lead the moment it arrives, with campaign attribution attached.',
  },
  {
    icon: IconShieldCheck,
    title: 'Role-scoped by design',
    body: 'Team Specialists see their own leads, managers see their team, admins see everything — enforced end to end.',
  },
  {
    icon: IconSliders,
    title: 'Fields that fit your intake',
    body: 'Add custom fields per programme without a deploy — they show up as filters and columns automatically.',
  },
];

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // Set by the forgot-password flow when it hands back to sign-in.
  const passwordReset = (useLocation().state as { passwordReset?: boolean } | null)?.passwordReset === true;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <LoginHero />

      <div className="login-panel">
        <div className="login-form-wrap">
          <div className="login-form-head">
            <h2>Welcome back</h2>
            <p>Sign in to pick up where you left off.</p>
          </div>

          {passwordReset && !error && (
            <div className="banner banner-success" role="status">
              Your password has been reset. Sign in with your new password.
            </div>
          )}
          <ErrorBanner error={error} />

          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <div className="input-icon">
                <IconMail width={16} height={16} />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  placeholder="you@organisation.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="field">
              <div className="label-row">
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" className="label-link">Forgot password?</Link>
              </div>
              <div className="input-icon">
                <IconLock width={16} height={16} />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    display: 'flex',
                    color: 'var(--muted)',
                  }}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IconEyeOff /> : <IconEye />}
                </button>
              </div>
            </div>
            <button className="btn btn-primary login-submit" disabled={busy}>
              {busy ? <span className="spinner" /> : (
                <>
                  Sign in <IconArrowRight width={15} height={15} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/** The brand panel beside the sign-in and forgot-password forms. */
export function LoginHero() {
  return (
    <div className="login-hero">
      <div className="login-hero-grid" aria-hidden="true" />

      <div className="login-hero-top">
        <div className="brand-mark" style={{ background: 'transparent', width: 36, height: 36, padding: 0 }}>
          <img src={logo} alt="Sisya Logo" style={{ width: '100%', height: '100%' }} />
        </div>
        <div>
          <b>SISYA CRM</b>
          <span>Admissions &amp; enquiry desk</span>
        </div>
      </div>

      <div className="login-hero-body">
        <h1>Every enquiry, routed to the right person in seconds.</h1>
        <p>
          One pipeline for every lead source, with automatic routing, a full activity timeline and
          reporting your counselling team actually looks at.
        </p>

        <div className="login-hero-features">
          {FEATURES.map((f) => (
            <div className="login-hero-feature" key={f.title}>
              <span className="ico"><f.icon width={16} height={16} /></span>
              <div>
                <b>{f.title}</b>
                <span>{f.body}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="login-hero-foot">Sisya CRM · Lead management for growing admissions teams</div>
    </div>
  );
}
